import React, { useState, useEffect } from 'react';
import { Code, Building, AlertTriangle, Check, X, Info } from 'lucide-react';
import Select from 'react-select';
import axios from 'axios';
import './StudentProfile.css';

const Technologies = ({ studentData, isEditing, editableData, onInputChange, showError, showSuccess }) => {
    const [technologiesData, setTechnologiesData] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [knownSkillsOptions, setKnownSkillsOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [pendingSkills, setPendingSkills] = useState([]);

    // Fetch technologies data on component mount
    useEffect(() => {
        fetchTechnologies();
    }, []);

    // Update department options when technologies data changes
    useEffect(() => {
        if (technologiesData.length > 0) {
            // Get unique departments
            const uniqueDepartments = [...new Set(technologiesData.map(item => item.Department))];
            const departmentOpts = uniqueDepartments.map(department => ({
                value: department,
                label: department
            }));
            setDepartmentOptions(departmentOpts);
        }
    }, [technologiesData]);

    // Update known skills options when department is selected
    useEffect(() => {
        if (editableData?.selectedDepartment) {
            // Get all skills for selected department (from all skill sets)
            const filteredSkills = technologiesData
                .filter(item => item.Department === editableData.selectedDepartment)
                .map(item => ({
                    value: item.skillKnown,
                    label: item.skillKnown
                }));

            // Remove duplicates
            const uniqueSkills = filteredSkills.filter((skill, index, self) =>
                index === self.findIndex((s) => s.value === skill.value)
            );

            setKnownSkillsOptions(uniqueSkills);
        } else {
            setKnownSkillsOptions([]);
        }
    }, [editableData?.selectedDepartment, technologiesData]);

    const fetchTechnologies = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/alltech`);

            if (response.data.success && response.data.data) {
                setTechnologiesData(response.data.data);
            } else {
                console.error('Failed to fetch technologies data');
                showError?.('Failed to fetch technologies data');
            }
        } catch (error) {
            console.error('Error fetching technologies:', error);
            showError?.('Error fetching technologies data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDepartmentChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : '';
        onInputChange?.('selectedDepartment', value);

        // Clear known skills when department changes
        onInputChange?.('selectedKnownSkills', []);
    };

    const handleKnownSkillsChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
        onInputChange?.('selectedKnownSkills', values);
    };

    // Helper function to get current department value for display
    const getCurrentDepartment = () => {
        // Try multiple sources for department data
        const departmentData = editableData?.selectedDepartment ||
            studentData?.selectedDepartment ||
            studentData?.courseDetails?.Department;

        if (typeof departmentData === 'string') {
            return departmentData.trim();
        }
        return '';
    };

    // Helper function to get current known skills values for display
    const getCurrentKnownSkills = () => {
        // Try multiple sources for known skills data
        const knownSkillsData = editableData?.selectedKnownSkills ||
            studentData?.selectedKnownSkills ||
            studentData?.courseDetails?.knownSkill;

        if (typeof knownSkillsData === 'string') {
            return knownSkillsData.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (Array.isArray(knownSkillsData)) {
            return knownSkillsData.filter(Boolean);
        }
        return [];
    };

    // Check if skills need approval
    const needsApproval = () => {
        const courseDetails = studentData?.courseDetails || {};
        return courseDetails.stuapprove === false || courseDetails.stuapprove === 0;
    };

    const handleApprovalClick = () => {
        const currentSkills = getCurrentKnownSkills();
        setPendingSkills(currentSkills);
        setShowApprovalModal(true);
    };


    const handleSkillApproval = async (approved) => {
        try {
            const token = localStorage.getItem("studentToken");

            // Fix: Use the correct backend route format
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/student/${studentData.studentId}/approve-skills`,
                {
                    approved: approved,
                    updatedBy: 'student'
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success) {
                showSuccess?.(approved ? 'Skills approved successfully!' : 'Skills changes rejected');

                // Update local data to reflect the new approval status
                const updatedStudentData = {
                    ...studentData,
                    courseDetails: {
                        ...studentData.courseDetails,
                        stuapprove: approved,
                        // Update timestamp to reflect when student made the decision
                        skillUpdateTimestamp: new Date().toISOString()
                    }
                };

                // Update localStorage with the new data
                localStorage.setItem('studentData', JSON.stringify(updatedStudentData));

                // Refresh the page to show updated data
                // Alternatively, you could update parent component state here
                window.location.reload();

            } else {
                throw new Error(response.data.message || 'Failed to update approval status');
            }
        } catch (error) {
            console.error('Approval error:', error);
            showError?.(error.response?.data?.message || 'Failed to update approval status');
        } finally {
            setShowApprovalModal(false);
        }
    };

    if (isLoading) {
        return (
            <div className="profile-section">
                <h2>Technologies</h2>
                <div className="loading-message">Loading technologies...</div>
            </div>
        );
    }

    const currentDepartment = getCurrentDepartment();
    const currentKnownSkills = getCurrentKnownSkills();
    const showApprovalIcon = needsApproval();

    return (
        <div className="profile-sections    ">
            {showApprovalIcon && !isEditing && (
                <div className="approval-status">
                    <button
                        className="approval-warning-btn"
                        onClick={handleApprovalClick}
                        title="Skills updated by staff - needs your approval"
                    >
                        <AlertTriangle size={20} className="warning-icon" />
                        <span>Skills Updated</span>
                    </button>
                </div>
            )}

            {/* Approval Modal */}
            {showApprovalModal && (
                <div className="modal-overlay">
                    <div className="approval-modal">
                        <div className="modal-header">
                            <h3>
                                <Info size={20} />
                                Skill Update Approval
                            </h3>
                            <button
                                className="close-btn"
                                onClick={() => setShowApprovalModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-content">
                            <p>Your skills have been updated by staff. Please review and approve or modify:</p>
                            <div className="skills-review">
                                <strong>Current Skills:</strong>
                                <ul className="skills-list">
                                    {pendingSkills.map((skill, index) => (
                                        <li key={index} className="skill-item pending">{skill}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="modal-actions">
                                <button
                                    className="approve-btn"
                                    onClick={() => handleSkillApproval(true)}
                                >
                                    <Check size={16} />
                                    Approve Changes
                                </button>
                                <button
                                    className="reject-btn"
                                    onClick={() => handleSkillApproval(false)}
                                >
                                    <X size={16} />
                                    I want to modify
                                </button>
                                <button
                                    className="cancel-btn"
                                    onClick={() => setShowApprovalModal(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="info-grid">
                <div className="info-item">
                    <label>Department</label>
                    <span>
                        <Building size={16} />
                        {currentDepartment || 'Not specified'}
                    </span>
                </div>


                <div className="info-item">
                    <label>
                        Known Skills
                        {showApprovalIcon && !isEditing && (
                            <AlertTriangle
                                size={16}
                                className="inline-warning-icon"
                                title="Updated by staff - needs approval"
                            />
                        )}
                    </label>

                    <span className={showApprovalIcon ? 'pending-approval' : ''}>
                        <Code size={16} />
                        {currentKnownSkills.length > 0
                            ? currentKnownSkills.join(', ')
                            : 'Not specified'}
                    </span>
                </div>


                {/* Display selected department and skills in non-editing mode */}
                {/* {!isEditing && currentDepartment && (
                    <div className="info-item full-width">
                        <label>Technology Summary</label>
                        <div className="technology-summary">
                            <div className="department-display">
                                <strong>Department:</strong> {currentDepartment}
                            </div>
                            {currentKnownSkills.length > 0 && (
                                <div className="known-skills-display">
                                    <strong>Known Skills:</strong>
                                    <ul className="skills-list">
                                        {currentKnownSkills.map((skill, index) => (
                                            <li
                                                key={index}
                                                className={`skill-item ${showApprovalIcon ? 'pending' : ''}`}
                                            >
                                                {skill}
                                                {showApprovalIcon && (
                                                    <AlertTriangle
                                                        size={12}
                                                        className="skill-warning-icon"
                                                    />
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )} */}
            </div>
        </div>
    );
};

export default Technologies;